import React, { useState, useEffect } from 'react';

/**
 * RENT pricing model (seller side):
 *
 * Seller enters ideal deposit + ideal rent.
 * total_cost = ideal_deposit + ideal_rent * CONVERSION (stored, never changes)
 *
 * If convertible=true, seller also provides:
 *   min_deposit — minimum deposit they'd accept (tenant can pay more rent instead)
 *   min_rent    — minimum rent they'd accept (tenant can pay more deposit instead)
 *
 * If convertible=false (or minimums not entered), ideal values are used as mins.
 *
 * Sent to backend: { total_cost, min_deposit, min_rent, convertible }
 */

const CONVERSION = 33; // 33M deposit ≈ 1M rent/month
const fmt = (v) => (v ? Number(v).toLocaleString('fa-IR') : '');

function AIPriceSuggestionStep({ onSubmit, initialData = {}, onPreviousStep, listingData }) {
  const [fairPrice, setFairPrice] = useState(initialData.fair_price || 0);
  const [loading, setLoading] = useState(false);

  // SALE
  const [askingPrice, setAskingPrice] = useState(initialData.asking_price || '');

  // RENT: ideal values
  const [idealDeposit, setIdealDeposit] = useState(initialData.ideal_deposit ?? '');
  const [idealRent, setIdealRent] = useState(initialData.ideal_rent ?? '');

  // RENT: convertible toggle + min stretch values
  const [convertible, setConvertible] = useState(initialData.convertible ?? false);
  const [minDeposit, setMinDeposit] = useState(initialData.min_deposit || '');
  const [minRent, setMinRent] = useState(initialData.min_rent || '');

  const [error, setError] = useState('');

  // Derived: total_cost from ideal values (fixed regardless of convertible)
  const totalCost = (idealDeposit && idealRent)
    ? Number(idealDeposit) + Number(idealRent) * CONVERSION
    : null;

  // Equivalent values shown as hints when convertible
  const rentIfMinDeposit = (minDeposit && totalCost)
    ? Math.max(0, Math.round((totalCost - Number(minDeposit)) / CONVERSION))
    : null;
  const depositIfMinRent = (minRent && totalCost)
    ? Math.max(0, totalCost - Number(minRent) * CONVERSION)
    : null;

  useEffect(() => {
    const simulatePricePrediction = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      const basePrice = listingData?.listing_type === 'RENT' ? 5000000 : 500000000;
      const area = listingData?.area || 100;
      const predicted = basePrice * area * (Math.random() * 0.5 + 0.8);
      setFairPrice(Math.round(Math.min(predicted, 9999999999)));
      setLoading(false);
    };
    simulatePricePrediction();
  }, [listingData]);

  const validate = () => {
    if (listingData?.listing_type !== 'RENT') return true;
    if (convertible) {
      if (idealDeposit && minDeposit && Number(minDeposit) > Number(idealDeposit)) {
        setError('حداقل ودیعه نمی‌تواند بیشتر از ودیعه ایده‌آل باشد.');
        return false;
      }
      if (idealRent && minRent && Number(minRent) > Number(idealRent)) {
        setError('حداقل اجاره نمی‌تواند بیشتر از اجاره ایده‌آل باشد.');
        return false;
      }
    }
    return true;
  };

  const handleContinue = () => {
    if (!validate()) return;
    setError('');

    const data = { fair_price: fairPrice };

    if (listingData?.listing_type === 'RENT') {
      // total_cost always derived from ideal values
      const computedTotalCost = totalCost;
      // mins fall back to ideal if not provided (or convertible is off)
      const finalMinDeposit = convertible && minDeposit
        ? parseFloat(minDeposit)
        : (idealDeposit ? parseFloat(idealDeposit) : null);
      const finalMinRent = convertible && minRent
        ? parseFloat(minRent)
        : (idealRent ? parseFloat(idealRent) : null);

      data.rent_details = {
        total_cost: computedTotalCost,
        min_deposit: finalMinDeposit,
        min_rent: finalMinRent,
        convertible,
      };
      data.user_price = idealRent ? parseFloat(idealRent) : null;
    } else if (listingData?.listing_type === 'SALE') {
      data.sale_details = {
        asking_price: askingPrice ? parseFloat(askingPrice) : null,
      };
      data.user_price = askingPrice ? parseFloat(askingPrice) : null;
    }

    onSubmit(data);
  };

  const formatPrice = (price) => new Intl.NumberFormat('fa-IR').format(price) + ' تومان';

  const userPrice = listingData?.listing_type === 'RENT'
    ? (idealRent ? parseFloat(idealRent) : null)
    : (askingPrice ? parseFloat(askingPrice) : null);

  return (
    <div className="ai-price-suggestion-step">
      <div className="step-header">
        <h3>پیشنهاد قیمت هوشمند</h3>
        <p>سیستم هوشمند ما بر اساس تحلیل داده‌های مکانی و زمانی قیمت پیشنهادی را محاسبه کرده است</p>
      </div>

      {loading ? (
        <div className="loading-section">
          <p>در حال تحلیل داده‌ها و محاسبه قیمت پیشنهادی...</p>
        </div>
      ) : (
        <>
          {/* ── AI prediction card ── */}
          <div className="prediction-section">
            <div className="prediction-card">
              <div className="prediction-header">
                <h4>پیشنهاد قیمت هوشمند</h4>
              </div>
              <div className="prediction-price">{formatPrice(fairPrice)}</div>
              <div className="prediction-description">
                این قیمت بر اساس تحلیل‌های مکانی، زمانی و مقایسه با املاک مشابه در منطقه محاسبه شده است
                {listingData?.listing_type === 'RENT' && (
                  <div className="price-note"><small>برای اجاره: این مبلغ اجاره ماهانه پیشنهادی است</small></div>
                )}
                {listingData?.listing_type === 'SALE' && (
                  <div className="price-note"><small>برای فروش: این مبلغ قیمت فروش پیشنهادی است</small></div>
                )}
              </div>
            </div>
          </div>

          {/* ════ RENT ════ */}
          {listingData?.listing_type === 'RENT' ? (
            <div className="price-input-section">

              {/* Section 1: ideal values */}
              <div className="s2-section-label">مقادیر ایده‌آل شما</div>
              <p className="s2-section-desc">ودیعه و اجاره‌ای که راحت‌ترین حالت برای شماست را وارد کنید.</p>

              <div className="s3-row">
                <div className="field-group">
                  <label className="field-label" htmlFor="ap-ideal-deposit">ودیعه ایده‌آل (تومان)</label>
                  <input
                    id="ap-ideal-deposit"
                    type="number"
                    min="0"
                    className="text-input"
                    placeholder="مثال: ۴۰۰٬۰۰۰٬۰۰۰"
                    value={idealDeposit}
                    onChange={e => setIdealDeposit(e.target.value)}
                  />
                  {idealDeposit && <small className="form-hint">{fmt(idealDeposit)} تومان</small>}
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor="ap-ideal-rent">اجاره ایده‌آل (تومان/ماه)</label>
                  <input
                    id="ap-ideal-rent"
                    type="number"
                    min="0"
                    className="text-input"
                    placeholder="مثال: ۳۰٬۰۰۰٬۰۰۰"
                    value={idealRent}
                    onChange={e => setIdealRent(e.target.value)}
                  />
                  {idealRent && <small className="form-hint">{fmt(idealRent)} تومان/ماه</small>}
                </div>
              </div>

              {/* Total cost pill */}
              {totalCost && (
                <div className="s2-total-box">
                  <span>هزینه کل معادل:</span>
                  <strong>{fmt(totalCost)} تومان</strong>
                  <span className="s2-total-note">
                    (هر ۳۳٬۳۳۳٬۳۳۳ تومان ودیعه = ۱٬۰۰۰٬۰۰۰ تومان اجاره)
                  </span>
                </div>
              )}

              {/* Convertible toggle */}
              <div className="field-group" style={{ marginTop: '1.2rem' }}>
                <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={convertible}
                    onChange={e => setConvertible(e.target.checked)}
                    style={{ width: '1.1rem', height: '1.1rem' }}
                  />
                  مبلغ رهن و اجاره قابل تبدیل است؟
                </label>
                <small className="form-hint">
                  در صورت فعال بودن، مستاجر می‌تواند با افزایش یکی از مبالغ، دیگری را کاهش دهد.
                </small>
              </div>

              {/* Section 2: min stretch values — only when convertible */}
              {convertible && (
                <>
                  <div className="s2-section-label s2-section-label--mt">حداقل مقادیر قابل قبول</div>

                  <div className="s3-row">
                    <div className="field-group">
                      <label className="field-label" htmlFor="ap-min-deposit">حداقل ودیعه (تومان)</label>
                      <input
                        id="ap-min-deposit"
                        type="number"
                        min="0"
                        className="text-input"
                        placeholder="مثال: ۲۰۰٬۰۰۰٬۰۰۰"
                        value={minDeposit}
                        onChange={e => setMinDeposit(e.target.value)}
                      />
                      {minDeposit && <small className="form-hint">{fmt(minDeposit)} تومان</small>}
                      {rentIfMinDeposit !== null && (
                        <small className="form-hint" style={{ color: '#888' }}>
                          اجاره معادل: {fmt(rentIfMinDeposit)} تومان/ماه
                        </small>
                      )}
                      <small className="form-hint">
                        در صورتی که تمایل دارید مقدار رهن را در صورت افزایش اجاره کاهش دهید، تا چه میزان می‌توانید کاهش دهید؟
                      </small>
                    </div>
                    <div className="field-group">
                      <label className="field-label" htmlFor="ap-min-rent">حداقل اجاره (تومان/ماه)</label>
                      <input
                        id="ap-min-rent"
                        type="number"
                        min="0"
                        className="text-input"
                        placeholder="مثال: ۲۰٬۰۰۰٬۰۰۰"
                        value={minRent}
                        onChange={e => setMinRent(e.target.value)}
                      />
                      {minRent && <small className="form-hint">{fmt(minRent)} تومان/ماه</small>}
                      {depositIfMinRent !== null && (
                        <small className="form-hint" style={{ color: '#888' }}>
                          ودیعه معادل: {fmt(depositIfMinRent)} تومان
                        </small>
                      )}
                      <small className="form-hint">
                        در صورتی که تمایل دارید مقدار اجاره را در صورت افزایش مبلغ رهن کاهش دهید، تا چه میزان می‌توانید کاهش دهید؟
                      </small>
                    </div>
                  </div>
                </>
              )}
            </div>

          ) : (
            /* ════ SALE ════ */
            <div className="price-input-section">
              <div className="field-group">
                <label className="field-label">قیمت فروش (تومان)</label>
                <input
                  type="number"
                  min="0"
                  value={askingPrice}
                  onChange={e => setAskingPrice(e.target.value)}
                  className="text-input"
                  placeholder="قیمت فروش ملک"
                />
                <small className="form-hint">
                  می‌توانید قیمت پیشنهادی سیستم را قبول کنید یا قیمت دلخواه خود را وارد کنید
                </small>
              </div>
            </div>
          )}

          {/* Price comparison */}
          {userPrice && (
            <div className="price-comparison">
              <div className="comparison-item">
                <span>قیمت پیشنهادی: </span>
                <strong>{formatPrice(fairPrice)}</strong>
              </div>
              <div className="comparison-item">
                <span>قیمت شما: </span>
                <strong>{formatPrice(userPrice)}</strong>
              </div>
              <div className="comparison-difference">
                {userPrice > fairPrice ? (
                  <span style={{ color: '#e74c3c' }}>بالاتر از پیشنهاد سیستم</span>
                ) : userPrice < fairPrice ? (
                  <span style={{ color: '#27ae60' }}>پایین‌تر از پیشنهاد سیستم</span>
                ) : (
                  <span style={{ color: '#3498db' }}>مطابق با پیشنهاد سیستم</span>
                )}
              </div>
            </div>
          )}

          {error && <p style={{ color: '#e74c3c', marginTop: '0.8rem' }}>{error}</p>}
        </>
      )}

      <div className="navigation-buttons">
        <button type="button" onClick={handleContinue} className="submit-button" disabled={loading}>
          {loading ? 'در حال پردازش...' : 'تأیید و ادامه'}
        </button>
        <button type="button" onClick={onPreviousStep} className="previous-button" disabled={loading}>
          بازگشت
        </button>
      </div>
    </div>
  );
}

export default AIPriceSuggestionStep;
